import React from 'react';
import {
    Tabs, Tab, IconButton, BottomNavigation,
    BottomNavigationAction, Box,
} from '@mui/material';
import {
    Brightness4, Brightness7,
    Dashboard as DashboardIcon,
    Analytics as AnalyticsIcon,
    Person as PersonIcon,
    AccountBalanceWalletOutlined as WalletIcon,
    School as SchoolIcon,
    Menu as MenuIcon,
    TrendingUp as InvestIcon,
    PersonOutlined as PersonOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/* ─── Brand ─────────────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    ink:     '#0D0A1A',
};

/* ─── Types / data (LOGIC UNCHANGED) ───────────────────────────── */
interface SharedNavBarProps {
    onToggleTheme: () => void;
    title?: string;
}

const navLinks = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { label: 'Activity',  path: '/donut',     icon: <AnalyticsIcon /> },
    { label: 'Wallet',    path: '/wallet',     icon: <WalletIcon />   },
    { label: 'Invest', path: '/invest', icon: <InvestIcon />},
    { label: 'Learn',     path: '/learn',      icon: <SchoolIcon />   },
];

const extraTitles: Record<string, string> = {
    '/spending-analysis': 'Spending Analysis',
    '/income':            'Income Analysis',
    '/reports':           'Reports',
};

const routeParentAlias: Record<string, string> = {
    '/dashboard':         '/',
    '/analysis':          '/donut',
    '/income':            '/donut',
    '/income-analysis':   '/donut',
    '/spending-analysis': '/donut',
    '/support': '/wallet',
    '/loans': '/wallet',
    '/card': '/wallet',
    '/goals':             'Goals',
    '/withdraw':          '/wallet',
    '/notifications': '/wallet',
    '/qr': '/wallet',
    '/transfer':          '/wallet',
    '/fund':              '/wallet',
    '/airtime':           '/wallet',
    '/refer':             '/wallet',
    '/bills':             '/wallet',
    '/history':           '/wallet',
    '/link-bank':         '/wallet',
    '/datapage':          '/wallet',
    '/invest/fund': '/invest',
    '/invest/stock': '/invest',
    '/savings': '/invest',
};

function mapPathToParent(pathname: string): string {
    if (navLinks.some(link => link.path === pathname)) return pathname;
    for (const [alias, parent] of Object.entries(routeParentAlias)) {
        if (pathname === alias || pathname.startsWith(alias + '/')) return parent;
    }
    const matchingParent = navLinks
        .filter(link => link.path !== '/')
        .find(link => pathname.startsWith(link.path));
    if (matchingParent) return matchingParent.path;
    return '/';
}

function getActiveIndex(pathname: string): number {
    const mappedPath = mapPathToParent(pathname);
    const exact = navLinks.findIndex(link => mappedPath === link.path);
    if (exact !== -1) return exact;
    const prefix = navLinks.findIndex(
        link => link.path !== '/' && mappedPath.startsWith(link.path)
    );
    return prefix !== -1 ? prefix : 0;
}

function getTitle(pathname: string): string {
    return (
        navLinks.find(link => pathname === link.path)?.label ||
        extraTitles[pathname] ||
        Object.entries(extraTitles).find(([p]) => pathname.startsWith(p))?.[1] ||
        navLinks.find(link => link.path !== '/' && pathname.startsWith(link.path))?.label ||
        'Dashboard'
    );
}

/* ─── Logo ──────────────────────────────────────────────────────── */
const Logo: React.FC<{ size?: number }> = ({ size = 40 }) => (
    <Box
        component="img"
        src="/images/ngc.png"
        alt="NG Credit"
        sx={{ width: size, height: size, borderRadius: `${size * 0.18}px`, display: 'block', flexShrink: 0 }}
    />
);

/* ─── Component ─────────────────────────────────────────────────── */
const SharedNavBar: React.FC<SharedNavBarProps> = ({ onToggleTheme }) => {
    const location = useLocation();
    const navigate  = useNavigate();
    const theme     = useTheme();
    const isMobile  = useMediaQuery('(max-width:600px)');

    const isDark      = theme.palette.mode === 'dark';
    const activeIndex = getActiveIndex(location.pathname);

    return (
        <>
            {/* ══ DESKTOP NAV ════════════════════════════════════════ */}
            {!isMobile && (
                <Box sx={{
                    position: 'sticky', top: 0, zIndex: 1200,
                    background: isDark
                        ? `linear-gradient(90deg, ${B.deep} 0%, ${B.dark} 100%)`
                        : `linear-gradient(90deg, ${B.dark} 0%, ${B.mid} 100%)`,
                    borderBottom: `1px solid ${alpha('#fff', .07)}`,
                    backdropFilter: 'blur(20px)',
                    px: 3,
                    display: 'flex', alignItems: 'center', height: 68,
                    gap: 2,
                }}>
                    {/* Hamburger */}
                    <IconButton size="small" onClick={() => navigate('/menu')}
                                sx={{ color: alpha('#fff', .45), '&:hover': { color: '#fff', background: alpha('#fff', .08) } }}>
                        <MenuIcon sx={{ fontSize: 22 }} />
                    </IconButton>

                    {/* Logo */}
                    <Box onClick={() => navigate('/')} sx={{ cursor: 'pointer', display: 'flex', mr: 1 }}>
                        <Logo size={36} />
                    </Box>

                    {/* Nav tabs */}
                    <Tabs
                        value={activeIndex}
                        onChange={(_, v) => navigate(navLinks[v].path)}
                        sx={{
                            flex: 1,
                            minHeight: 68,
                            '& .MuiTabs-indicator': {
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                                background: `linear-gradient(90deg, ${B.purple}, ${B.light})`,
                                boxShadow: `0 0 12px ${alpha(B.purple, .6)}`,
                            },
                            '& .MuiTab-root': {
                                color: alpha('#fff', .38),
                                fontFamily: "'DM Sans', system-ui, sans-serif",
                                fontWeight: 600,
                                fontSize: '.9rem',
                                letterSpacing: '.01em',
                                textTransform: 'none',
                                minHeight: 68,
                                px: 2.5,
                                transition: 'color .2s',
                                '&:hover': {
                                    color: alpha('#fff', .8),
                                    '& .MuiTouchRipple-root': { opacity: 0 },
                                },
                                '&.Mui-selected': { color: '#fff' },
                            },
                        }}
                    >
                        {navLinks.map((link, i) => (
                            <Tab key={i} label={link.label} />
                        ))}
                    </Tabs>

                    {/* Profile */}
                    <IconButton size="small" onClick={() => navigate('/profile')}
                                sx={{ color: alpha('#fff', .45), ml: 'auto', '&:hover': { color: '#fff', background: alpha('#fff', .08) } }}>
                        <PersonOutlined sx={{ fontSize: 20 }} />
                    </IconButton>

                    {/* Theme toggle */}
                    <IconButton size="small" onClick={onToggleTheme}
                                sx={{ color: alpha('#fff', .45), ml: 'auto', '&:hover': { color: '#fff', background: alpha('#fff', .08) } }}>
                        {isDark
                            ? <Brightness7 sx={{ fontSize: 20 }} />
                            : <Brightness4 sx={{ fontSize: 20 }} />
                        }
                    </IconButton>
                </Box>
            )}

            {/* ══ MOBILE ═════════════════════════════════════════════ */}
            {isMobile && (
                <>
                    {/* ── Top header bar ── */}
                    <Box sx={{
                        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300,
                        height: 56,
                        background: `linear-gradient(90deg, ${B.deep} 0%, ${B.dark} 60%, ${B.mid} 100%)`,
                        borderBottom: `1px solid ${alpha('#fff', .07)}`,
                        backdropFilter: 'blur(20px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1,
                    }}>
                        {/* Hamburger — left */}
                        <IconButton onClick={() => navigate('/menu')}
                                    sx={{ color: alpha('#fff', .5), '&:hover': { color: '#fff', background: alpha('#fff', .08) } }}>
                            <MenuIcon sx={{ fontSize: 22 }} />
                        </IconButton>

                        {/* Logo — centre */}
                        <Box onClick={() => navigate('/')}
                             sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', cursor: 'pointer', display: 'flex' }}>
                            <Logo size={38} />
                        </Box>

                        {/* Right: profile + theme toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={() => navigate('/profile')}
                                        sx={{ color: alpha('#fff', .5), '&:hover': { color: '#fff', background: alpha('#fff', .08) } }}>
                                <PersonOutlined sx={{ fontSize: 20 }} />
                            </IconButton>
                            <IconButton onClick={onToggleTheme}
                                        sx={{ color: alpha('#fff', .5), '&:hover': { color: '#fff', background: alpha('#fff', .08) } }}>
                                {isDark ? <Brightness7 sx={{ fontSize: 20 }} /> : <Brightness4 sx={{ fontSize: 20 }} />}
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Bottom nav */}
                    <Box sx={{
                        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
                        background: isDark ? B.deep : '#fff',
                        borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 -8px 32px ${alpha(B.dark, isDark ? .4 : .07)}`,
                    }}>
                        {/* Animated active indicator bar */}
                        <Box sx={{ display: 'flex', pointerEvents: 'none' }}>
                            {navLinks.map((_, i) => (
                                <Box key={i} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{
                                        width: i === activeIndex ? 36 : 0,
                                        height: 3,
                                        borderRadius: '0 0 3px 3px',
                                        background: `linear-gradient(90deg, ${B.purple}, ${B.light})`,
                                        boxShadow: i === activeIndex ? `0 0 10px ${alpha(B.purple, .55)}` : 'none',
                                        transition: 'width .35s cubic-bezier(.16,1,.3,1)',
                                    }} />
                                </Box>
                            ))}
                        </Box>

                        <BottomNavigation
                            showLabels
                            value={activeIndex}
                            onChange={(_, newValue) => {
                                if (typeof newValue === 'number' && newValue < navLinks.length) {
                                    navigate(navLinks[newValue].path);
                                }
                            }}
                            sx={{
                                height: 72,
                                bgcolor: 'transparent',
                                '& .MuiBottomNavigationAction-root': {
                                    paddingTop: '10px',
                                    paddingBottom: '8px',
                                    minWidth: 0,
                                    flex: 1,
                                    color: alpha(isDark ? '#fff' : B.ink, .25),
                                    fontFamily: "'DM Sans', system-ui, sans-serif",
                                    transition: 'color .2s',
                                    '& .MuiBottomNavigationAction-label': {
                                        fontSize: '.68rem',
                                        fontWeight: 600,
                                        letterSpacing: '.02em',
                                        marginTop: '3px',
                                        opacity: '1 !important',
                                    },
                                    '& .MuiSvgIcon-root': { fontSize: 22 },
                                },
                                '& .Mui-selected': {
                                    color: `${B.purple} !important`,
                                    '& .MuiSvgIcon-root': {
                                        filter: `drop-shadow(0 0 6px ${alpha(B.purple, .5)})`,
                                    },
                                },
                            }}
                        >
                            {navLinks.map((link, i) => (
                                <BottomNavigationAction
                                    key={i}
                                    value={i}
                                    label={link.label}
                                    icon={link.icon}
                                />
                            ))}
                        </BottomNavigation>
                    </Box>
                </>
            )}
        </>
    );
};

export default SharedNavBar;